import { fileDownloadConfig, httpClient } from '@/httpClient';
import { api } from '@/api';
import config from '@/config'; // eslint-disable-line import/extensions
import { getFilesAsNameAndUrl } from '../../../utils/getFilesAsNameAndUrl';
import { downloadFile } from '../../../utils/downloadFile';

export default {
  namespaced: true,
  state: {
    items: [],
    total: 0,
    perPage: 15,
    isLoading: false,

    current: {
      isLoading: false,
    },

    uploadModal: {
      isVisible: false,
      isOpening: false,
    },

    isArchiveCreating: false,
    isSourcesArchiveCreating: false,
    isOriginalPlansArchiveCreating: false,

    plans: {
      isLoading: false,
      items: [],
      pagination: null,
    },
  },
  mutations: {
    setApartments(state, programs) {
      state.items = programs;
    },
    setLoading(state, loading) {
      state.isLoading = loading;
    },
    setPagination(state, { total, perPage }) {
      state.total = total;
      state.perPage = perPage;
    },
    setUploadModalVisibility(state, visibility) {
      state.uploadModal.isVisible = visibility;
    },
    setUploadModelOpening(state, openState) {
      state.uploadModal.isOpening = openState;
    },
    setCurrent(state, data) {
      const identifiers = data.additional?.id
        ? data.additional.id.map((id) => id.trim())
        : [];

      state.current = {
        isLoading: state.current.isLoading,
        ...data,
        status: Number(data.status),
        identifiers,
      };
    },
    setPlansLoading(state, value) {
      state.plans.isLoading = value;
    },
    setPlansItems(state, items) {
      state.plans.items = items;
    },
    setPlansPagination(state, pagination) {
      state.plans.pagination = pagination;
    },
    setCurrentLoading(state, loading) {
      state.current.isLoading = loading;
    },
    setCurrentId(state, id) {
      state.current.id = id;
    },
    setCurrentName(state, name) {
      state.current.name = name;
    },
    setIsArchiveCreating(state, isCreating) {
      state.isArchiveCreating = isCreating;
    },
    setIsSourcesArchiveCreating(state, isCreating) {
      state.isSourcesArchiveCreating = isCreating;
    },
    setIsOriginalPlansArchiveCreating(state, isCreating) {
      state.isOriginalPlansArchiveCreating = isCreating;
    },
  },
  actions: {
    getList(store, filter = {}) {
      store.commit('setLoading', true);

      const params = {
        page: filter.page,
        per_page: filter.perPage,
      };

      if (Object.keys(filter).length !== 0) {
        const normalizedFilter = {
          name: filter.name ?? null,
          crm_tag_id: filter.crmTagId ?? null,
          after_date: filter.dates?.[0]?.toISOString() ?? null,
          before_date: filter.dates?.[1]?.toISOString() ?? null,
          priority: filter.priorityId ?? null,
          status: filter.statusId ?? null,
          responsible_ninja: filter.responsibleNinja ?? null,
          responsible_ninja_3d: filter.responsibleNinja3d ?? null,
          responsible_reviewer: filter.responsibleReviewer ?? null,
          responsible_reviewer_3d: filter.responsibleReviewer3d ?? null,
          address: filter.address ?? null,
          complex: filter.complex ?? null,
          developer: filter.developer ?? null,
          region: filter.region ?? null,
          project_type: filter.projectType ?? null,
        };

        const entries = Object.entries(normalizedFilter);

        const filledFilterEntries = entries.filter(([, value]) => {
          if (Array.isArray(value)) {
            return value.length > 0;
          }

          return value !== null && value !== undefined && value !== '';
        });

        if (filledFilterEntries.length) {
          params.filter = Object.fromEntries(filledFilterEntries);
        }
      }

      return api.getApartmentList(params, filter)
        .then(({ data }) => {
          store.commit('setPagination', data);
          store.commit('setApartments', data.items);
        }).finally(() => store.commit('setLoading', false));
    },
    getPlans({ commit }, { id, type, params = {} }) {
      commit('setPlansLoading', true);

      return httpClient.get(`/api/crm/apartments/${id}/plans/${type}`, { params })
        .then(({ data }) => {
          commit('setPlansItems', data.plans);
          commit('setPlansPagination', data.pagination);
          return data;
        })
        .finally(() => commit('setPlansLoading', false));
    },
    getPlansLegacy({ commit }, { id, type }) {
      commit('setPlansLoading', true);

      return api.getApartmentsPlansList(id, type).then(({ data }) => {
        commit('setPlansItems', data.plans);
        commit('setCurrentId', data.id);
        commit('setCurrentName', data.name);
        return data.plans;
      }).finally(() => commit('setPlansLoading', false));
    },
    get({ commit }, id) {
      commit('setCurrentLoading', true);

      return httpClient.get(`/api/crm/apartments/${id}`).then(({ data }) => {
        commit('setCurrent', data);
        commit('setCurrentLoading', false);
      });
    },
    openUploadModal({ commit }) {
      commit('setUploadModalVisibility', true);
    },
    closeUploadModal({ commit }) {
      commit('setUploadModalVisibility', false);
    },
    create({ commit, state }, {
      name, ids, pictures, description, sameNinjaFor2dAnd3dPlans, integrationWithBitrix, projectType,
      processingBrp, reviewBrp, defaultPresetId, defaultUnrealEnvironmentId,
    }) {
      const fd = new FormData();
      fd.append('name', name);
      if (ids) {
        ids.split(',').forEach((id) => fd.append('additional[id][]', id.trim()));
      }
      if (description) {
        fd.append('description', description);
      }
      if (sameNinjaFor2dAnd3dPlans !== undefined && sameNinjaFor2dAnd3dPlans !== null) {
        fd.append('same_ninja_for_2d_and_3d_plans', sameNinjaFor2dAnd3dPlans ? '1' : '0');
      }
      if (integrationWithBitrix !== undefined && integrationWithBitrix !== null) {
        fd.append('integration_with_bitrix', integrationWithBitrix ? '1' : '0');
      }
      if (projectType !== undefined && projectType !== null) {
        fd.append('project_type', projectType);
      }
      if (processingBrp !== undefined && processingBrp !== null) {
        fd.append('processing_brp', processingBrp);
      }
      if (reviewBrp !== undefined && reviewBrp !== null) {
        fd.append('review_brp', reviewBrp);
      }
      if (defaultPresetId) fd.append('default_preset_id', defaultPresetId);
      if (defaultUnrealEnvironmentId) fd.append('default_unreal_environment_id', defaultUnrealEnvironmentId);
      pictures.forEach((picture, index) => fd.append(`floor_data[${index}]`, picture));

      return httpClient.post('/api/crm/apartments', fd).then(({ data }) => {
        commit('setApartments', [
          data,
          ...state.items,
        ]);
      });
    },
    update({ commit, state }, {
      name, description, additional, region, developer, complex, address, sameNinjaFor2dAnd3dPlans,
      sameReviewerFor2dAnd3dPlans, integrationWithBitrix, projectType, widgetBrandingId,
      defaultPresetId, defaultUnrealEnvironmentId,
    }) {
      const { id } = state.current;
      return api.updateApartment(id, {
        name,
        description,
        additional,
        region,
        developer,
        complex,
        address,
        sameNinjaFor2dAnd3dPlans: sameNinjaFor2dAnd3dPlans ? 1 : 0,
        sameReviewerFor2dAnd3dPlans: sameReviewerFor2dAnd3dPlans ? 1 : 0,
        integrationWithBitrix: integrationWithBitrix ? 1 : 0,
        projectType,
        widgetBrandingId,
        defaultPresetId,
        defaultUnrealEnvironmentId,
      }).then(({ data }) => {
        commit('setCurrent', data);
      });
    },
    updateDeadline({ commit, state }, payload) {
      const { id } = state.current;
      return api.updateApartmentDeadline(id, payload).then(({ data }) => {
        commit('setCurrent', data);
      });
    },
    sendInProgress({ dispatch, state }) {
      const { id } = state.current;
      return httpClient.post(`/api/crm/apartments/${id}/send_in_progress`).then(() => {
        dispatch('get', id);
      });
    },
    updateResponsibles({ commit, state }, responsibles) {
      const { id } = state.current;
      return api.updateApartmentResponsibles(id, responsibles)
        .then(({ data }) => commit('setCurrent', data));
    },
    uploadFile({ commit, state }, file) {
      const { id } = state.current;
      const fd = new FormData();
      fd.append('file', file);
      return httpClient.post(`/api/crm/apartments/${id}/file`, fd).then(({ data }) => {
        commit('setCurrent', data);
      });
    },
    deleteImage({ dispatch, state }, path) {
      const { id } = state.current;
      const fileName = path.replace(`${config.apiUrl}/crm_apartments_storage/${id}/files/`, '');
      return httpClient.delete(`/api/crm/apartments/${id}/file?file=${fileName}`).then(() => {
        dispatch('get', id);
      });
    },
    downloadPlansArchive({ commit }, id) {
      commit('setIsArchiveCreating', true);

      httpClient.get(`/api/crm/apartments/${id}/download`, fileDownloadConfig)
        .then(({ data }) => downloadFile(data, `apartment_${id}.zip`))
        .finally(() => commit('setIsArchiveCreating', false));
    },
    downloadSourcesArchive({ commit }, id) {
      commit('setIsSourcesArchiveCreating', true);

      httpClient.get(`/api/crm/apartments/${id}/download_source_files`, fileDownloadConfig)
        .then(({ data }) => downloadFile(data, `apartment_sources_${id}.zip`))
        .finally(() => commit('setIsSourcesArchiveCreating', false));
    },
    downloadOriginalPlansArchive({ commit }, id) {
      commit('setIsOriginalPlansArchiveCreating', true);

      return api.getOriginalPlansArchive(id)
        .then(({ data }) => downloadFile(data, `apartment_original_plans_${id}.zip`))
        .finally(() => commit('setIsOriginalPlansArchiveCreating', false));
    },
    async changePriority({ commit, state }, priority) {
      const { data } = await api.changeApartmentPriority(state.current.id, priority);

      commit('setCurrent', data);

      return data;
    },
    loadPlans({ commit, state }, { type, params = {} }) {
      commit('setPlansLoading', true);

      const apartmentId = state.current.id;
      if (!apartmentId) {
        console.error('No apartment ID found');
        commit('setPlansLoading', false);
        return Promise.reject(new Error('No apartment ID found'));
      }

      const queryParams = {
        per_page: params.per_page || 15,
        page: params.page || 1,
      };

      if (params.name) queryParams.name = params.name;
      if (params.status) queryParams.status = params.status;
      if (params.ninja_ids) queryParams.ninja_ids = params.ninja_ids;
      if (params.manager_ids) queryParams.manager_ids = params.manager_ids;

      return httpClient.get(`/api/crm/apartments/${apartmentId}/plans/${type}`, {
        params: queryParams,
      })
        .then(({ data }) => {
          commit('setPlansItems', data.plans);
          commit('setPlansPagination', data.pagination);
          return data;
        })
        .catch((error) => {
          console.error('Error loading plans:', error);
          throw error;
        })
        .finally(() => commit('setPlansLoading', false));
    },
    enablePlans3d({ commit, state }, { ids }) {
      return httpClient.post('/api/crm/plans/enable-3d', { ids })
        .then(() => {
        })
        .catch((error) => {
          console.error('Error enabling 3D for plans:', error);
          throw error;
        });
    },
    changePlansPreset({ commit, state }, { ids, presetId }) {
      return httpClient.post('/api/crm/plans/change-preset', { ids, preset_id: presetId })
        .then(() => {
        })
        .catch((error) => {
          console.error('Error changing preset for plans:', error);
          throw error;
        });
    },
  },
  getters: {
    filesList: (state) => {
      if (!state.current || !state.current.files) {
        return [];
      }

      return getFilesAsNameAndUrl(state.current.files);
    },
    plansPagination: (state) => state.plans.pagination,
    plansItems: (state) => state.plans.items,
    plansLoading: (state) => state.plans.isLoading,
    hasPlans: (state) => state.plans.items && state.plans.items.length > 0,
    totalPlansCount: (state) => (state.plans.pagination ? state.plans.pagination.total : 0),
  },
};
